import React, { useState, useEffect, useRef, useCallback } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useFeatureFlag } from "@harnessio/ff-react-client-sdk";
import {
  Grid,
  Box,
  Typography,
  Button,
  Alert,
  useMediaQuery,
  CircularProgress,
  LinearProgress,
  Skeleton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import axios from "axios";
import DeviceInfoAndLocation from "../../global/DeviceInfoAndLocation";
import TechRelevanceFormDark from "../TechRelevanceFormDark";
import Loading from "./Loading";
import ErrorComponent from "./Error";
import {
  checkYear,
  checkSkillType,
  getTopSkill,
  DataEncryption,
  DataDecryption,
} from "../../utils/relevanceHelpers";
import { getEmail } from "../../utils/storage";
import {
  storeSkillsInLocalStorage,
  storeRelevanceResult,
} from "../../utils/skillsUtils";
import ReactGA4 from "react-ga4";
import SubSkills from "../Subskills";
import LearnAndEarnSuggestion from "./Learn-and-earn-suggestion";
import PopularityChart from "../../PopularityChartV2";
import IndustrialUsageChart from "../../IndustrialUsageChart";
import toast from "react-hot-toast";

// Function to render custom icons
const renderCustomIcon = (iconName, size = 24) => {
  const iconPath = `/assets/images/bookmark-icons/${iconName}.svg`;
  return (
    <img
      src={iconPath}
      alt={iconName}
      style={{
        width: size,
        height: size,
        objectFit: "contain",
      }}
      onError={(e) => {
        console.warn(`Failed to load custom icon: ${iconName}`);
        e.target.style.display = "none";
      }}
    />
  );
};

function BasicRelevanceCheckDark({
  isLoggedIn,
  onLoginClick,
  onViewFullInsights,
}) {
  const isMobile = useMediaQuery("(max-width:600px)");
  const methods = useForm({ criteriaMode: "all" });

  // Feature flags
  const staticRandomEncodeDecodeString = useFeatureFlag(
    "staticRandomEncodeDecodeString"
  );
  const secretEncodeDecodeString = useFeatureFlag("secretEncodeDecodeString");

  // State
  const [relevanceResult, setRelevanceResult] = useState("");
  const [loadingButton, setLoadingButton] = useState(false);
  const [autoValue, setAutoValue] = useState([]);
  const [errorAPI, setErrorAPI] = useState(false);
  const [errorWithStatus, setErrorWithStatus] = useState(null);
  const [location, setLocation] = useState();
  const [deviceInfo, setDeviceInfo] = useState();
  const [skillLoading, setSkillLoading] = useState(false);
  const [topSkillValidity, setTopSkillValidity] = useState([]);
  const [synonyms, setSynonyms] = useState([]);
  const [addSkill, setAddSkill] = useState(false);
  const [shouldAutoFocus, setShouldAutoFocus] = useState(false);
  const [skillLength, setSkillLength] = useState(0);
  const [shouldAutoClick, setShouldAutoClick] = useState(false);
  const [skillValidationComplete, setSkillValidationComplete] = useState(false);
  const [subSkills, setSubSkills] = useState([]);
  const [chatGPTData, setChatGPTData] = useState("");
  const [chatGptButton, setChatGptButton] = useState(false);
  const [processingSkill, setProcessingSkill] = useState(null);
  const [selectedSkill, setSelectedSkill] = useState("");
  const [chatgptError, setChatgptError] = useState(false);

  const autoClickExecutedRef = useRef(false);
  const isSubmittingRef = useRef(false);
  const cancelToken = useRef(null);
  const cancelChatGPTToken = useRef(null);
  const autoAskAIClickedRef = useRef(false);

  // Get device info and location
  useEffect(() => {
    DeviceInfoAndLocation(setLocation, setDeviceInfo);
  }, []);

  // Callback to handle skill validation completion
  const handleSkillValidationComplete = useCallback(() => {
    setSkillValidationComplete(true);
  }, []);

  // Function to get skill options (for TechRelevanceFormDark)
  function getSkillOptions(
    setData,
    endpoint,
    setLoading,
    setErrorWithStatus,
    setErrorAPI
  ) {
    setData([]);
    setLoading(true);

    if (cancelToken.current) {
      cancelToken.current.cancel("Previous request cancelled");
    }

    try {
      cancelToken.current = axios.CancelToken.source();

      axios
        .get(process.env.REACT_APP_API_BASE_URL + endpoint, {
          cancelToken: cancelToken.current.token,
        })
        .then((object) => {
          if (object?.status === 201) {
            const decryptedData = DataDecryption(
              object.data,
              secretEncodeDecodeString,
              staticRandomEncodeDecodeString
            );
            // Remove "Other" option
            const filteredData = Array.isArray(decryptedData)
              ? decryptedData.filter((n) => n.name !== "Other")
              : [];
            setData(filteredData);
          } else {
            setData([]);
          }
          setLoading(false);
          setErrorAPI(false);
        })
        .catch((error) => {
          if (axios.isCancel(error)) {
            console.info("Request canceled:", error.message);
          } else {
            let errorMessage = "An unexpected error occurred";
            let errorStatus = null;

            if (error?.response) {
              errorStatus = error.response.status;
              if (errorStatus === 500) {
                errorMessage = "Server error occurred. Please try again later.";
              } else if (errorStatus === 404) {
                errorMessage = "Endpoint not found. Please check the URL.";
              } else if (errorStatus >= 400) {
                errorMessage = "Client-side error occurred.";
              }
            } else {
              errorMessage = "Network error, please check your connection.";
            }

            setErrorWithStatus({ message: errorMessage, status: errorStatus });
            setErrorAPI(true);
            setTimeout(() => {
              setLoading(false);
            }, 2000);
          }
        });
    } catch (error) {
      setErrorWithStatus({
        message: "An unexpected error occurred",
        status: null,
      });
      setTimeout(() => {
        setLoading(false);
      }, 2000);
      setErrorAPI(true);
    }
  }

  // Tech relevance handler
  const onSubmit = useCallback(
    async (data) => {
      // Prevent multiple simultaneous submissions
      if (isSubmittingRef.current && loadingButton) {
        return;
      }

      if (autoValue.length === 0) {
        methods.setError("primarySkill", {
          type: "required",
          message: `Please Enter a skill`,
        });
        setRelevanceResult("");
        return;
      }

      // Set submitting flag
      isSubmittingRef.current = true;
      setLoadingButton(true);
      setErrorAPI(false);
      setSkill({});
      setCheckSkill("");

      // Email is optional - only include if user is logged in
      const userEmail = getEmail();
      if (userEmail) {
        data.email = userEmail;
      }
      data.userLocation = location;
      data.deviceInfo = deviceInfo;
      data.primarySkill = data.primarySkill;

      setSkillLength(methods.getValues("primarySkill")?.length);

      try {
        axios
          .post(
            process.env.REACT_APP_API_BASE_URL + "/get-relevanceResult",
            DataEncryption(
              data,
              secretEncodeDecodeString,
              staticRandomEncodeDecodeString
            ),
            {
              options: {
                type: "json",
              },
            }
          )
          .then((object) => {
            const decryptedData = DataDecryption(
              object.data,
              secretEncodeDecodeString,
              staticRandomEncodeDecodeString
            );
            setAddSkill(false);
            setRelevanceResult(decryptedData);
            setLoadingButton(false);
            // Reset auto-click flag for new results
            autoAskAIClickedRef.current = false;

            // Reset submitting flag on success
            isSubmittingRef.current = false;

            // Track key conversion event in GA4 only when result is successfully saved to MongoDB
            if (decryptedData?.relevanceResultId) {
              ReactGA4.event("check_relevance_homepage", {
                primary_skill: decryptedData?.topPrimarySkill?.[0]?.name || "",
                relevance_result_id: decryptedData.relevanceResultId,
              });
            }

            // Store skills for transfer to /skill-relevance page
            if (decryptedData?.topPrimarySkill) {
              const skillNames = decryptedData.topPrimarySkill.map(
                (skill) => skill.name
              );
              storeSkillsInLocalStorage(skillNames);
            }

            // Store full relevance result data to avoid duplicate API call
            storeRelevanceResult(decryptedData);

            // Scroll to results
            setTimeout(() => {
              const element = document?.getElementById(
                "home-relevance-results-dark"
              );
              element?.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 100);
          })
          .catch((error) => {
            let errorMessage = "An unexpected error occurred";
            let errorStatus = null;

            if (error?.response) {
              errorStatus = error.response.status;
              if (
                errorStatus === 500 &&
                error?.response?.data?.message === "Email verification failed"
              ) {
                errorMessage = "Email not valid !!";
                methods.setError("email", {
                  type: "validation",
                  message: errorMessage,
                });
                setRelevanceResult("");
              } else if (errorStatus === 404) {
                errorMessage = "API endpoint not found";
              } else if (errorStatus >= 400) {
                errorMessage = "Client-side error occurred";
              } else if (errorStatus >= 500) {
                errorMessage = "Internal server error occurred";
              }
            } else {
              errorMessage = "Network error, please check your connection";
            }
            setErrorWithStatus({ message: errorMessage, status: errorStatus });
            setErrorAPI(true);
            setLoadingButton(false);
            isSubmittingRef.current = false;
          });
      } catch (error) {
        setErrorWithStatus({
          message: "An unexpected error occurred",
          status: null,
        });
        setLoadingButton(false);
        setErrorAPI(true);
        isSubmittingRef.current = false;
      }
    },
    [
      autoValue,
      loadingButton,
      methods,
      location,
      deviceInfo,
      secretEncodeDecodeString,
      staticRandomEncodeDecodeString,
    ]
  );

  // Dummy state setters for TechRelevanceFormDark compatibility
  const [skill, setSkill] = useState({});
  const [checkSkill, setCheckSkill] = useState("");

  // Handle Ask AI click
  const handleSendClick = useCallback(
    async (item) => {
      setProcessingSkill(item.name);
      setChatGptButton(true);
      setChatgptError(false);
      setSkill(item);
      setCheckSkill(item?.name);
      setSubSkills(item?.subSkill || []);
      setSelectedSkill(item.name);

      try {
        if (cancelChatGPTToken.current) {
          cancelChatGPTToken.current.cancel("Previous request cancelled");
        }

        cancelChatGPTToken.current = axios.CancelToken.source();

        axios
          .post(
            process.env.REACT_APP_API_BASE_URL + "/get-chatgpt-response",
            DataEncryption(
              {
                skillName: item?.name,
                resultId: relevanceResult?.relevanceResultId,
              },
              secretEncodeDecodeString,
              staticRandomEncodeDecodeString
            ),
            {
              cancelToken: cancelChatGPTToken.current.token,
              options: {
                type: "json",
              },
            }
          )
          .then((object) => {
            setChatGptButton(false);
            setProcessingSkill(null);
            const data = DataDecryption(
              object.data,
              secretEncodeDecodeString,
              staticRandomEncodeDecodeString
            );
            let gptdata = JSON.parse(data?.result);

            if (!gptdata || Object.keys(gptdata).length === 0) {
              setChatgptError(true);
              setChatGPTData("");
              toast.error("No data available, please try again.");
            } else {
              // Track key conversion event in GA4 only when AI response succeeds
              ReactGA4.event("ask_ai_homepage", {
                skill_name: item.name,
                relevance_result_id: relevanceResult?.relevanceResultId || "",
              });

              setChatGPTData(gptdata);
            }
          })
          .catch((error) => {
            setProcessingSkill(null);
            if (axios.isCancel(error)) {
              console.info("Request canceled:", error.message);
            } else {
              let errorMessage = "Something went wrong. Please try again.";
              if (error?.response) {
                if (error.response.status === 500) {
                  errorMessage =
                    "Server error occurred. Please try again later.";
                } else if (error.response.status === 404) {
                  errorMessage = "Endpoint not found. Please check the URL.";
                } else if (error.response.status >= 400) {
                  errorMessage = "Client-side error occurred.";
                }
              } else {
                errorMessage = "Network error, please check your connection.";
              }

              setChatgptError(true);
              toast.error(errorMessage);
              setTimeout(() => {
                setChatGptButton(false);
              }, 2000);
            }
          });
      } catch (error) {
        setProcessingSkill(null);
        setChatgptError(true);
        toast.error("An unexpected error occurred. Please try again.");
        setTimeout(() => {
          setChatGptButton(false);
        }, 2000);
        console.error("Unexpected Error:", error);
      }
    },
    [relevanceResult, secretEncodeDecodeString, staticRandomEncodeDecodeString]
  );

  // Auto-click "Ask AI" for first skill when results are available
  useEffect(() => {
    if (
      relevanceResult?.topPrimarySkill?.length > 0 &&
      !autoAskAIClickedRef.current &&
      !loadingButton &&
      !chatGptButton &&
      handleSendClick
    ) {
      const firstSkill = relevanceResult.topPrimarySkill[0];
      if (firstSkill) {
        // Small delay to ensure UI is ready
        const timer = setTimeout(() => {
          handleSendClick(firstSkill);
          autoAskAIClickedRef.current = true;
        }, 1000);
        return () => clearTimeout(timer);
      }
    }
  }, [relevanceResult, loadingButton, chatGptButton, handleSendClick]);

  return (
    <Box
      sx={{
        backgroundColor: "#000",
        color: "#fff",
        pt: { xs: 3, sm: 4, md: 5, lg: 6 },
        pb: { xs: 6, sm: 8, md: 10, lg: 12 },
        px: { xs: 2, sm: 3, md: 4, lg: 6 },
        overflow: "hidden",
        scrollMarginTop: { xs: "80px", sm: "80px", md: "0px" },
      }}
      id="check-relevance-section-dark"
    >
      <Grid container justifyContent="center">
        <Grid item xs={12} sm={11} md={10} lg={9} xl={8}>
          {/* Title Section */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: { xs: 1.5, sm: 2, md: 2.5 },
              mb: { xs: 2.5, sm: 3, md: 3.5 },
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                mb: 0,
                mt: 0,
              }}
            >
              {/* {renderCustomIcon("enter-skills", 60)} */}
              <Typography
                sx={{
                  fontFamily: "poppins",
                  fontWeight: 700,
                  fontSize: { xs: "24px", sm: "30px", md: "45px" },
                  textAlign: "center",
                  wordBreak: "break-word",
                  color: "#FFF",
                }}
              >
                Enter any <span style={{ color: "#6652E4" }}>topic</span>,{" "}
                <span style={{ color: "#6652E4" }}>domain</span>,{" "}
                <span style={{ color: "#6652E4" }}>skill</span>,{" "}
                <span style={{ color: "#6652E4" }}>role</span>, or{" "}
                <span style={{ color: "#6652E4" }}>activity</span>
              </Typography>
            </Box>
          </Box>

          {/* Form Section */}
          <FormProvider {...methods}>
            <form
              id="home-tech-relevance-dark"
              onSubmit={methods.handleSubmit(onSubmit)}
            >
              <TechRelevanceFormDark
                setSkillLoading={setSkillLoading}
                setRelevanceResult={setRelevanceResult}
                methods={methods}
                DataDecryption={(jsonString) =>
                  DataDecryption(
                    jsonString,
                    secretEncodeDecodeString,
                    staticRandomEncodeDecodeString
                  )
                }
                DataEncryption={(json) =>
                  DataEncryption(
                    json,
                    secretEncodeDecodeString,
                    staticRandomEncodeDecodeString
                  )
                }
                setTopSkillValidity={setTopSkillValidity}
                setSynonyms={setSynonyms}
                setAutoValue={setAutoValue}
                shouldAutoFocus={shouldAutoFocus}
                setShouldAutoFocus={setShouldAutoFocus}
                getEmail={getEmail}
                skillLength={skillLength}
                autoValue={autoValue}
                topSkillValidity={topSkillValidity}
                deviceInfo={deviceInfo}
                addSkill={addSkill}
                onSubmit={onSubmit}
                setErrorWithStatus={setErrorWithStatus}
                setErrorAPI={setErrorAPI}
                location={location}
                getSkillOptions={getSkillOptions}
                skillLoading={skillLoading}
                shouldAutoClick={shouldAutoClick}
                autoClickExecutedRef={autoClickExecutedRef}
                onSkillValidationComplete={handleSkillValidationComplete}
              />

              {/* Powered by ChatGPT Section */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  my: { xs: 1.5, sm: 2, md: 2.5 },
                  flexShrink: 0,
                  width: "100%",
                  opacity: 0.8,
                }}
              >
                <Box
                  sx={{
                    maxWidth: {
                      xs: "20px",
                      sm: "24px",
                      md: "28px",
                      lg: "32px",
                    },
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <img
                    src="/assets/images/chat-gpt-logo.svg"
                    alt="powered-by-chat-gpt"
                    loading="lazy"
                    style={{
                      maxWidth: "100%",
                      width: "100%",
                      height: "auto",
                    }}
                  />
                </Box>
                <Box sx={{ px: { xs: 0.5, sm: 0.75, md: 1 } }}>
                  <Typography
                    sx={{
                      color: "rgba(255, 255, 255, 0.7)",
                      fontSize: {
                        xs: "9px",
                        sm: "10px",
                        md: "11px",
                        lg: "12px",
                      },
                      fontFamily:
                        "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                      whiteSpace: "nowrap",
                      fontWeight: 400,
                    }}
                  >
                    Powered by ChatGPT
                  </Typography>
                </Box>
              </Box>

              <Box py={4}>
                {loadingButton ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      my: 5,
                    }}
                  >
                    <Loading />
                  </Box>
                ) : (
                  <Button
                    id={"check-relevance-button-homepage-dark"}
                    type="submit"
                    form="home-tech-relevance-dark"
                    sx={{
                      maxWidth: { xs: "100%", sm: "350px", md: "400px" },
                      width: "100%",
                      display: "block",
                      m: "0px auto",
                      color: "#FFFFFF",
                      py: { xs: "12px", sm: "14px", md: "16px" },
                      px: { xs: "24px", sm: "32px", md: "40px" },
                      fontSize: {
                        xs: "16px",
                        sm: "18px",
                        md: "20px",
                        lg: "24px",
                      },
                      fontWeight: 700,
                      borderRadius: "30px",
                      textTransform: "none",
                      fontStyle: "bold",
                      lineHeight: {
                        xs: "24px",
                        sm: "27px",
                        md: "30px",
                      },
                      backgroundColor: "transparent !important",
                      backgroundImage:
                        "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1) )",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                      transition: "all 0.3s linear",
                      fontFamily:
                        "'Montserrat', 'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                      boxShadow: "0px 4px 12px rgba(102, 82, 228, 0.3)",
                      "&:hover": {
                        border: "1px solid rgba(255, 255, 255, 0.4)",
                        background: "#FFFFFF !important",
                        color: "rgba(108,82,229,1)",
                        transition: "all 0.3s linear",
                        boxShadow: "0px 6px 16px rgba(102, 82, 228, 0.5)",
                      },
                      "&:disabled": {
                        opacity: 0.6,
                        cursor: "not-allowed",
                        color: "rgba(255, 255, 255, 0.5)",
                      },
                    }}
                    disabled={skillLoading}
                  >
                    Career Map
                  </Button>
                )}
              </Box>
            </form>
          </FormProvider>

          {/* Error Display */}
          {errorAPI && (
            <Box sx={{ mt: 2 }}>
              <ErrorComponent errorWithStatus={errorWithStatus} />
            </Box>
          )}

          {/* Results Section */}
          {relevanceResult?.length !== 0 &&
          relevanceResult !== undefined &&
          !loadingButton &&
          relevanceResult?.topPrimarySkill?.length > 0 ? (
            <Box id="home-relevance-results-dark" sx={{ mt: 6 }}>
              {/* Results Title */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: { xs: 1.5, sm: 2 },
                  mb: { xs: 4, sm: 5, md: 6 },
                  flexWrap: "wrap",
                }}
              >
                <Box
                  sx={{
                    display: { xs: "none", sm: "block" },
                    maxWidth: { xs: "35px", sm: "40px" },
                  }}
                >
                  {renderCustomIcon("relevance-result", 40)}
                </Box>
                <Typography
                  sx={{
                    fontFamily:
                      "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                    fontSize: {
                      xs: "24px",
                      sm: "32px",
                      md: "40px",
                      lg: "48px",
                    },
                    lineHeight: {
                      xs: "28px",
                      sm: "38px",
                      md: "48px",
                      lg: "58px",
                    },
                    textAlign: "center",
                    fontWeight: 600,
                    color: "#fff",
                    letterSpacing: { xs: "-0.02em", md: "-0.01em" },
                  }}
                >
                  How long you will remain relevant ?
                </Typography>
              </Box>

              <Grid
                container
                spacing={{ xs: 2, sm: 3, md: 4 }}
                rowSpacing={{ xs: 6, sm: 6, md: 6, lg: 6 }}
              >
                {/* Top Skill Validity Card */}
                <Grid item xs={12} md={6}>
                  {(() => {
                    const topSkillData = getTopSkill(
                      relevanceResult?.topPrimarySkill
                    );
                    const expiryYear = topSkillData?.maxExpiryYear;
                    const currentYear = new Date().getFullYear();
                    const yearsRemaining =
                      expiryYear > currentYear ? expiryYear - currentYear : 0;
                    const isTrending = expiryYear > currentYear;
                    const isCurrent =
                      expiryYear >= currentYear - 1 &&
                      expiryYear <= currentYear;
                    const isOutdated =
                      expiryYear < currentYear - 1 &&
                      expiryYear >= currentYear - 4;
                    const isObsolete = expiryYear < currentYear - 4;

                    // Determine status icon and color
                    let statusIcon = null;
                    let statusColor = "#6652E4";
                    if (isTrending) {
                      statusIcon = (
                        <TrendingUpIcon
                          sx={{ fontSize: "24px", color: "#4caf50" }}
                        />
                      );
                      statusColor = "#4caf50";
                    } else if (isCurrent) {
                      statusIcon = (
                        <CheckCircleIcon
                          sx={{ fontSize: "24px", color: "#ff9800" }}
                        />
                      );
                      statusColor = "#ff9800";
                    } else if (isOutdated) {
                      statusIcon = (
                        <WarningIcon
                          sx={{ fontSize: "24px", color: "#ffc107" }}
                        />
                      );
                      statusColor = "#ffc107";
                    } else if (isObsolete) {
                      statusIcon = (
                        <ErrorOutlineIcon
                          sx={{ fontSize: "24px", color: "#f44336" }}
                        />
                      );
                      statusColor = "#f44336";
                    }

                    return (
                      <Box
                        sx={{
                          p: { xs: "24px", sm: "28px", md: "32px", lg: "36px" },
                          border: `1px solid ${statusColor}40`,
                          borderRadius: { xs: "24px", sm: "32px", md: "39px" },
                          boxShadow: `0px 0px 16px 0px ${statusColor}20`,
                          height: "100%",
                          backgroundColor: "rgba(255, 255, 255, 0.05)",
                          backdropFilter: "blur(10px)",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: `0px 0px 24px 0px ${statusColor}30`,
                            transform: "translateY(-2px)",
                          },
                        }}
                      >
                        {/* Header with Icon and Title */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            mb: { xs: 2, sm: 2.5 },
                          }}
                        >
                          {statusIcon}
                          <Typography
                            sx={{
                              fontFamily:
                                "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                              fontSize: {
                                xs: "18px",
                                sm: "20px",
                                md: "22px",
                                lg: "24px",
                              },
                              fontWeight: 600,
                              color: "#fff",
                            }}
                          >
                            Career Relevance Timeline
                          </Typography>
                        </Box>

                        {/* Year Display with Context */}
                        <Box sx={{ mb: 2 }}>
                          <Typography
                            sx={{
                              fontFamily:
                                "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                              fontSize: { xs: "14px", sm: "15px", md: "16px" },
                              fontWeight: 400,
                              color: "rgba(255, 255, 255, 0.7)",
                              mb: 0.5,
                            }}
                          >
                            Your skills remain relevant until
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "baseline",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            <Typography
                              sx={{
                                fontFamily:
                                  "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                fontSize: {
                                  xs: "48px",
                                  sm: "56px",
                                  md: "64px",
                                  lg: "72px",
                                },
                                fontWeight: 700,
                                color: statusColor,
                                lineHeight: "1",
                              }}
                            >
                              {expiryYear}
                            </Typography>
                            {yearsRemaining > 0 && (
                              <Typography
                                sx={{
                                  fontFamily:
                                    "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                  fontSize: {
                                    xs: "16px",
                                    sm: "18px",
                                    md: "20px",
                                  },
                                  fontWeight: 500,
                                  color: "rgba(255, 255, 255, 0.8)",
                                  ml: { xs: 0, sm: 1 },
                                }}
                              >
                                ({yearsRemaining}{" "}
                                {yearsRemaining === 1 ? "year" : "years"}{" "}
                                remaining)
                              </Typography>
                            )}
                          </Box>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontSize: { xs: "12px", sm: "13px" },
                              display: "block",
                              mt: 0.5,
                              color: "rgba(255, 255, 255, 0.6)",
                              fontStyle: "italic",
                            }}
                          >
                            *Estimated based on industry trends
                          </Typography>
                        </Box>

                        {/* Status Message */}
                        <Box
                          sx={{
                            mt: 2.5,
                            p: { xs: "12px", sm: "14px", md: "16px" },
                            borderRadius: "12px",
                            backgroundColor: "rgba(255, 255, 255, 0.03)",
                            border: `1px solid ${statusColor}30`,
                            "& .MuiTypography-root": {
                              color: "#fff !important",
                              fontFamily:
                                "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important",
                              fontSize: {
                                xs: "14px !important",
                                sm: "15px !important",
                                md: "16px !important",
                              },
                              lineHeight: "1.6 !important",
                            },
                            // Override inline styles for dark mode
                            "& span[style*='#232323']": {
                              color: "#fff !important",
                            },
                            "& span[style*='#26801E']": {
                              color: "#4caf50 !important",
                              fontWeight: "600 !important",
                            },
                            "& span[style*='darkorange']": {
                              color: "#ff9800 !important",
                              fontWeight: "600 !important",
                            },
                            "& span[style*='#ffc107']": {
                              color: "#ffc107 !important",
                              fontWeight: "600 !important",
                            },
                            "& span[style*='red']": {
                              color: "#f44336 !important",
                              fontWeight: "600 !important",
                            },
                          }}
                        >
                          {topSkillData?.labelSuggestion}
                        </Box>
                      </Box>
                    );
                  })()}
                </Grid>

                {/* Skill Score Card */}
                <Grid item xs={12} md={6}>
                  <Box
                    sx={{
                      p: { xs: "20px", sm: "25px", md: "30px", lg: "35px" },
                      border: "2px solid rgba(102,82,228,0.3)",
                      borderRadius: { xs: "24px", sm: "32px", md: "39px" },
                      boxShadow: "0px 0px 16px 0px rgba(102,82,228,0.2)",
                      height: "100%",
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      backdropFilter: "blur(10px)",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: "0px 0px 24px 0px rgba(102,82,228,0.3)",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        mb: 3,
                        justifyContent: { xs: "center", md: "flex-start" },
                      }}
                    >
                      <Box sx={{ maxWidth: { xs: "46px", md: "58px" } }}>
                        <img
                          src="/assets/images/skill-score-relevance-01.svg"
                          width="100%"
                          height="auto"
                          alt="skill-score"
                          loading="lazy"
                        />
                      </Box>
                      <Typography
                        sx={{
                          fontFamily:
                            "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                          fontWeight: 600,
                          fontSize: {
                            xs: "20px",
                            sm: "24px",
                            md: "28px",
                            lg: "32px",
                          },
                          color: "#fff",
                        }}
                      >
                        Skill score
                      </Typography>
                    </Box>

                    {autoValue?.length >= 5 && (
                      <Alert
                        severity="success"
                        sx={{
                          mb: 2,
                          backgroundColor: "rgba(46, 125, 50, 0.2)",
                          border: "1px solid rgba(46, 125, 50, 0.3)",
                          color: "#fff",
                          "& .MuiAlert-icon": {
                            color: "#4caf50",
                          },
                        }}
                      >
                        Displaying the top 5 skills based on the most recent
                        validity year.
                      </Alert>
                    )}

                    {relevanceResult?.topPrimarySkill?.map((item, index) => {
                      return (
                        <Box
                          key={item._id || index}
                          sx={{
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            gap: { xs: 2, sm: 3 },
                            alignItems: { xs: "flex-start", sm: "center" },
                            justifyContent: "space-between",
                            my: { xs: "20px", md: "25px" },
                          }}
                        >
                          <Box sx={{ flex: 1, width: "100%" }}>
                            <Typography
                              sx={{
                                fontFamily:
                                  "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                fontSize: { xs: "18px", sm: "20px" },
                                fontWeight: 600,
                                mb: { xs: "6px", sm: "8px" },
                                color: "#fff",
                              }}
                            >
                              {item?.name}
                            </Typography>
                            <Box
                              sx={{
                                display: "flex",
                                gap: { xs: 1.5, sm: 2 },
                                alignItems: "center",
                                justifyContent: "space-between",
                                mb: 1,
                                flexWrap: { xs: "wrap", sm: "nowrap" },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: { xs: "14px", sm: "16px" },
                                  color: "rgba(255, 255, 255, 0.8)",
                                  fontFamily:
                                    "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                }}
                              >
                                {item.type === "y"
                                  ? checkYear(item?.expiryYear)?.label
                                  : checkSkillType(item?.expiryYear)}
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: { xs: "20px", sm: "22px" },
                                  fontWeight: 700,
                                  color:
                                    checkYear(item?.expiryYear)
                                      ?.percentageColor || "#6652E4",
                                  fontFamily:
                                    "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                }}
                              >
                                {item?.score <= 0
                                  ? "20"
                                  : item?.score > 100
                                  ? "80"
                                  : Math.round(item?.score)}
                                %
                              </Typography>
                            </Box>
                            <Box sx={{ width: "100%" }}>
                              <LinearProgress
                                sx={{
                                  height: "10px",
                                  borderRadius: "20px",
                                  "& .MuiLinearProgress-bar": {
                                    backgroundColor:
                                      checkYear(item?.expiryYear)
                                        ?.barPrimaryColor || "#6652E4",
                                  },
                                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                                }}
                                variant="determinate"
                                value={
                                  item?.score <= 0
                                    ? 20
                                    : item?.score > 100
                                    ? 80
                                    : Math.round(item?.score)
                                }
                              />
                            </Box>
                            {/* Ask AI Button */}
                            <Box sx={{ mt: 2, width: "100%" }}>
                              <Button
                                variant="contained"
                                onClick={() => handleSendClick(item)}
                                disabled={processingSkill !== null}
                                sx={{
                                  width: "100%",
                                  maxWidth: { xs: "100%", sm: "200px" },
                                  color: "#fff",
                                  py: { xs: "10px", sm: "12px" },
                                  px: { xs: "20px", sm: "24px" },
                                  fontSize: { xs: "14px", sm: "16px" },
                                  fontWeight: 700,
                                  borderRadius: "27px",
                                  textTransform: "none",
                                  backgroundColor: "transparent !important",
                                  backgroundImage:
                                    "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1))",
                                  border: "1px solid rgba(74,61,158,1)",
                                  transition: "all 0.3s linear",
                                  "&:hover": {
                                    border: "1px solid rgba(74,61,158,1)",
                                    background: "#FFF !important",
                                    color: "rgba(108,82,229,1)",
                                    transition: "all 0.3s linear",
                                  },
                                  "&:disabled": {
                                    background: "rgba(108,82,229,0.5)",
                                    color: "#fff",
                                    cursor: "not-allowed",
                                  },
                                }}
                              >
                                {processingSkill === item.name ? (
                                  <CircularProgress
                                    size={20}
                                    sx={{ color: "#fff" }}
                                  />
                                ) : (
                                  "Ask AI"
                                )}
                              </Button>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Grid>

                {/* Info Alert */}
                {/* <Grid item xs={12}>
                  <Alert
                    severity="info"
                    sx={{
                      backgroundColor: "rgba(102,82,228,0.1)",
                      border: "1px solid rgba(102,82,228,0.3)",
                      borderRadius: { xs: "24px", sm: "32px", md: "39px" },

                      color: "#fff",
                      // mt: { xs: 6, sm: 6, md: 10 },
                      // mb: { xs: 3, sm: 4, md: 0 },
                      // mx: { xs: 0, sm: 1, md: 2 },
                      px: { xs: 2.5, sm: 3.5, md: 4 },
                      py: { xs: 2, sm: 2.5, md: 3 },
                      "& .MuiAlert-icon": {
                        color: "#6652E4",
                      },
                    }}
                  >
                    Recommendations and potential salary increase percentages
                    provided are for informational purposes only and should be
                    used as suggestions, not guarantees
                  </Alert>
                </Grid> */}

                {/* Enterprise Adoption, Popularity, and Learn & Earn Section */}
                {checkSkill !== "" && chatGPTData && !chatgptError && (
                  <Grid
                    item
                    xs={12}
                    md={12}
                    sx={{ mt: { xs: 4, sm: 5, md: 6 } }}
                  >
                    <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
                      {/* Enterprise Adoption */}
                      {chatGPTData?.percentageUsage && (
                        <Grid item xs={12} sm={6} md={4} lg={4}>
                          <Box
                            sx={{
                              border: "1px solid rgba(102,82,228,0.3)",
                              borderRadius: {
                                xs: "24px",
                                sm: "32px",
                                md: "39px",
                              },
                              background: "rgba(255, 255, 255, 0.05)",
                              backdropFilter: "blur(10px)",
                              p: { xs: 3, sm: 4, md: 5 },
                              height: "100%",
                              boxShadow:
                                "0px 0px 16px 0px rgba(102,82,228,0.2)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                boxShadow:
                                  "0px 0px 24px 0px rgba(102,82,228,0.3)",
                                transform: "translateY(-2px)",
                              },
                            }}
                            className="enterprise-adoption"
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mb: 3,
                                justifyContent: "center",
                              }}
                            >
                              {renderCustomIcon("enterprice", 40)}
                              <Typography
                                sx={{
                                  fontFamily:
                                    "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                  fontSize: {
                                    xs: "20px",
                                    sm: "24px",
                                    md: "24px",
                                    lg: "28px",
                                  },
                                  fontWeight: 600,
                                  color: "#fff",
                                  textAlign: "center",
                                }}
                              >
                                Enterprise Adoption
                              </Typography>
                            </Box>
                            {chatGptButton ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  my: 5,
                                }}
                              >
                                <CircularProgress sx={{ color: "#6652E4" }} />
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                <IndustrialUsageChart
                                  value={parseInt(
                                    chatGPTData?.percentageUsage || 10
                                  )}
                                  color="#6652E4"
                                  textColor="#fff"
                                  trackColor="rgba(255, 255, 255, 0.1)"
                                />
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      )}

                      {/* Popularity */}
                      {chatGPTData?.popularityRating && (
                        <Grid item xs={12} sm={6} md={4} lg={4}>
                          <Box
                            sx={{
                              border: "1px solid rgba(102,82,228,0.3)",
                              borderRadius: {
                                xs: "24px",
                                sm: "32px",
                                md: "39px",
                              },
                              background: "rgba(255, 255, 255, 0.05)",
                              backdropFilter: "blur(10px)",
                              p: { xs: 3, sm: 4, md: 5 },
                              height: "100%",
                              boxShadow:
                                "0px 0px 16px 0px rgba(102,82,228,0.2)",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                boxShadow:
                                  "0px 0px 24px 0px rgba(102,82,228,0.3)",
                                transform: "translateY(-2px)",
                              },
                            }}
                            className="skill-popularity"
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mb: 3,
                                justifyContent: "center",
                              }}
                            >
                              {renderCustomIcon("popularity", 40)}
                              <Typography
                                sx={{
                                  fontFamily:
                                    "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                  fontSize: {
                                    xs: "20px",
                                    sm: "24px",
                                    md: "24px",
                                    lg: "28px",
                                  },
                                  fontWeight: 600,
                                  color: "#fff",
                                  textAlign: "center",
                                }}
                              >
                                Popularity
                              </Typography>
                            </Box>
                            {chatGptButton ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                  my: 5,
                                }}
                              >
                                <CircularProgress sx={{ color: "#6652E4" }} />
                              </Box>
                            ) : (
                              <Box pt={{ xs: 0, md: 0, lg: 2 }}>
                                <PopularityChart
                                  value={
                                    parseInt(chatGPTData?.popularityRating) *
                                      10 || 10
                                  }
                                  color="#6652E4"
                                  textColor="#fff"
                                />
                              </Box>
                            )}
                          </Box>
                        </Grid>
                      )}

                        {/* Latest Technologies */}
                        {chatGPTData?.latestTechnologies && (
                          <Grid item xs={12} sm={12} md={4} lg={4}>
                            <Box
                              sx={{
                                border: "1px solid rgba(102,82,228,0.3)",
                                borderRadius: { xs: "24px", sm: "32px", md: "39px" },
                                background: "rgba(255, 255, 255, 0.05)",
                                backdropFilter: "blur(10px)",
                                p: { xs: 3, sm: 4, md: 5 },
                                height: "100%",
                                boxShadow: "0px 0px 16px 0px rgba(102,82,228,0.2)",
                                transition: "all 0.3s ease",
                                "&:hover": {
                                  boxShadow: "0px 0px 24px 0px rgba(102,82,228,0.3)",
                                  transform: "translateY(-2px)",
                                },
                              }}
                              id="learn-earn-dark"
                              className="lear-earn-recommendations"
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 2,
                                  mb: 3,
                                  justifyContent: "center",
                                }}
                              >
                                {renderCustomIcon("learn-earn", 40)}
                                <Typography
                                  sx={{
                                    fontFamily:
                                      "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                                    fontSize: {
                                      xs: "20px",
                                      sm: "24px",
                                      md: "24px",
                                      lg: "28px",
                                    },
                                    fontWeight: 600,
                                    color: "#fff",
                                    textAlign: "center",
                                  }}
                                >
                                  Latest Technologies
                                </Typography>
                              </Box>
                              <LearnAndEarnSuggestion chatGPTData={chatGPTData} darkMode={true} />
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                  )}

                {/* Subskills Section */}
                {checkSkill !== "" &&
                  (subSkills.length > 0 || chatGptButton) &&
                  !chatgptError && (
                    <Grid item xs={12} md={12} sx={{ mt: { xs: 4, sm: 5, md: 6 } }}>
                      <Box
                        sx={{
                          border: "1px solid rgba(102,82,228,0.3)",
                          borderRadius: { xs: "24px", sm: "32px", md: "39px" },
                          background: "rgba(255, 255, 255, 0.05)",
                          backdropFilter: "blur(10px)",
                          p: { xs: 3, sm: 4, md: 5 },
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 3,
                            justifyContent: "center",
                          }}
                        >
                          {renderCustomIcon("subskills", 40)}
                          <Typography
                            sx={{
                              fontFamily:
                                "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                              fontSize: {
                                xs: "24px",
                                sm: "28px",
                                md: "32px",
                                lg: "36px",
                              },
                              fontWeight: 600,
                              color: "#fff",
                              textAlign: "center",
                            }}
                          >
                            Subskills for {checkSkill}
                          </Typography>
                        </Box>
                        <SubSkills
                          subSkills={subSkills}
                          chatGptButton={chatGptButton}
                          loop={false}
                        />
                      </Box>
                    </Grid>
                  )}

                {/* Full Insights Skeletons - Always visible before CTA */}
                {relevanceResult?.topPrimarySkill?.length > 0 && (
                  <>
                    {/* Suggested Roles Skeleton */}
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={4}
                      sx={{ mt: { xs: 4, sm: 5, md: 6 } }}
                    >
                      <Box
                        sx={{
                          p: { xs: "20px", sm: "25px", md: "30px" },
                          borderRadius: { xs: "24px", sm: "32px", md: "39px" },
                          background: "rgba(255, 255, 255, 0.05)",
                          border: "1px solid rgba(102,82,228,0.3)",
                          backdropFilter: "blur(10px)",
                          minHeight: 250,
                          position: "relative",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily:
                              "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                            fontSize: { xs: "18px", sm: "20px", md: "22px" },
                            fontWeight: 600,
                            color: "#fff",
                            mb: 2,
                            textAlign: "center",
                          }}
                        >
                          Suggested Roles
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Skeleton
                              width="80%"
                              height={28}
                              sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <Skeleton
                              width="80%"
                              height={28}
                              sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                            />
                          </Grid>
                          {[...Array(3)].map((_, idx) => (
                            <React.Fragment key={idx}>
                              <Grid item xs={6}>
                                <Box
                                  sx={{ display: "flex", alignItems: "center" }}
                                >
                                  <Skeleton
                                    variant="circular"
                                    width={16}
                                    height={16}
                                    sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                                  />
                                  <Skeleton
                                    width="60%"
                                    height={24}
                                    sx={{
                                      ml: 1,
                                      bgcolor: "rgba(255, 255, 255, 0.1)",
                                    }}
                                  />
                                </Box>
                              </Grid>
                              <Grid item xs={6}>
                                <Skeleton
                                  width="60%"
                                  height={24}
                                  sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                                />
                              </Grid>
                            </React.Fragment>
                          ))}
                        </Grid>
                        <Box
                          sx={{
                            mt: 3,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            variant="contained"
                            onClick={() => {
                              ReactGA4.event("view_insights_click", {
                                section: "suggested_roles",
                              });
                              if (isLoggedIn) {
                                if (onViewFullInsights) {
                                  onViewFullInsights();
                                }
                              } else {
                                onLoginClick();
                              }
                            }}
                            sx={{
                              backgroundColor: "transparent !important",
                              backgroundImage:
                                "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1) )",
                              color: "#fff",
                              fontFamily:
                                "'Montserrat', 'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                              fontSize: { xs: "12px", sm: "13px", md: "14px" },
                              fontWeight: 600,
                              textTransform: "none",
                              borderRadius: "20px",
                              px: { xs: 2, sm: 2.5, md: 3 },
                              py: { xs: "6px", sm: "8px", md: "10px" },
                              boxShadow: "0px 2px 8px rgba(102,82,228,0.3)",
                              transition: "all 0.3s linear",
                              "&:hover": {
                                border: "1px solid rgba(74,61,158,1)",
                                background: "#FFF !important",
                                color: "rgba(108,82,228,1)",
                                boxShadow: "0px 4px 12px rgba(102,82,228,0.4)",
                                transition: "all 0.3s linear",
                              },
                            }}
                          >
                            View
                          </Button>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Other Roles with Responsibilities Skeleton */}
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={4}
                      sx={{ mt: { xs: 4, sm: 5, md: 6 } }}
                    >
                      <Box
                        sx={{
                          p: { xs: "20px", sm: "25px", md: "30px" },
                          borderRadius: { xs: "24px", sm: "32px", md: "39px" },
                          background: "rgba(255, 255, 255, 0.05)",
                          border: "1px solid rgba(102,82,228,0.3)",
                          backdropFilter: "blur(10px)",
                          minHeight: 250,
                          position: "relative",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily:
                              "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                            fontSize: { xs: "18px", sm: "20px", md: "22px" },
                            fontWeight: 600,
                            color: "#fff",
                            mb: 2,
                            textAlign: "center",
                          }}
                        >
                          Other Roles with Responsibilities
                        </Typography>
                        {[...Array(3)].map((_, idx) => (
                          <Accordion
                            key={idx}
                            sx={{
                              backgroundColor: "transparent",
                              boxShadow: "none",
                              mb: 1,
                              border: "1px solid rgba(102,82,228,0.2)",
                              borderRadius: "12px",
                            }}
                          >
                            <AccordionSummary
                              expandIcon={
                                <Skeleton
                                  variant="circular"
                                  width={24}
                                  height={24}
                                  sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                                />
                              }
                            >
                              <Skeleton
                                width="60%"
                                height={24}
                                sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                              />
                            </AccordionSummary>
                            <AccordionDetails>
                              <Skeleton
                                width="90%"
                                height={18}
                                sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                              />
                              <Skeleton
                                width="80%"
                                height={18}
                                sx={{
                                  bgcolor: "rgba(255, 255, 255, 0.1)",
                                  mt: 0.5,
                                }}
                              />
                            </AccordionDetails>
                          </Accordion>
                        ))}
                        <Box
                          sx={{
                            mt: 3,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            variant="contained"
                            onClick={() => {
                              ReactGA4.event("view_insights_click", {
                                section: "other_roles",
                              });
                              if (isLoggedIn) {
                                if (onViewFullInsights) {
                                  onViewFullInsights();
                                }
                              } else {
                                onLoginClick();
                              }
                            }}
                            sx={{
                              backgroundColor: "transparent !important",
                              backgroundImage:
                                "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1) )",
                              color: "#fff",
                              fontFamily:
                                "'Montserrat', 'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                              fontSize: { xs: "12px", sm: "13px", md: "14px" },
                              fontWeight: 600,
                              textTransform: "none",
                              borderRadius: "20px",
                              px: { xs: 2, sm: 2.5, md: 3 },
                              py: { xs: "6px", sm: "8px", md: "10px" },
                              boxShadow: "0px 2px 8px rgba(102,82,228,0.3)",
                              transition: "all 0.3s linear",
                              "&:hover": {
                                border: "1px solid rgba(74,61,158,1)",
                                background: "#FFF !important",
                                color: "rgba(108,82,228,1)",
                                boxShadow: "0px 4px 12px rgba(102,82,228,0.4)",
                                transition: "all 0.3s linear",
                              },
                            }}
                          >
                            View
                          </Button>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Courses Skeleton */}
                    <Grid
                      item
                      xs={12}
                      sm={12}
                      md={4}
                      sx={{ mt: { xs: 4, sm: 5, md: 6 } }}
                    >
                      <Box
                        sx={{
                          p: { xs: "20px", sm: "25px", md: "30px" },
                          borderRadius: { xs: "24px", sm: "32px", md: "39px" },
                          background: "rgba(255, 255, 255, 0.05)",
                          border: "1px solid rgba(102,82,228,0.3)",
                          backdropFilter: "blur(10px)",
                          minHeight: 220,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          position: "relative",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily:
                              "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                            fontSize: { xs: "18px", sm: "20px", md: "22px" },
                            fontWeight: 600,
                            color: "#fff",
                            mb: 3,
                            textAlign: "center",
                          }}
                        >
                          Courses
                        </Typography>
                        <Box
                          sx={{
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            gap: 2,
                          }}
                        >
                          {[...Array(4)].map((_, idx) => (
                            <Skeleton
                              key={idx}
                              variant="rectangular"
                              height={44}
                              sx={{
                                borderRadius: "12px",
                                width: "100%",
                                maxWidth: 320,
                                mx: "auto",
                                bgcolor: "rgba(255, 255, 255, 0.1)",
                              }}
                            />
                          ))}
                        </Box>
                        <Box
                          sx={{
                            mt: 3,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            variant="contained"
                            onClick={() => {
                              ReactGA4.event("view_insights_click", {
                                section: "courses",
                              });
                              if (isLoggedIn) {
                                if (onViewFullInsights) {
                                  onViewFullInsights();
                                }
                              } else {
                                onLoginClick();
                              }
                            }}
                            sx={{
                              backgroundColor: "transparent !important",
                              backgroundImage:
                                "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1) )",
                              color: "#fff",
                              fontFamily:
                                "'Montserrat', 'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                              fontSize: { xs: "12px", sm: "13px", md: "14px" },
                              fontWeight: 600,
                              textTransform: "none",
                              borderRadius: "20px",
                              px: { xs: 2, sm: 2.5, md: 3 },
                              py: { xs: "6px", sm: "8px", md: "10px" },
                              boxShadow: "0px 2px 8px rgba(102,82,228,0.3)",
                              transition: "all 0.3s linear",
                              "&:hover": {
                                border: "1px solid rgba(74,61,158,1)",
                                background: "#FFF !important",
                                color: "rgba(108,82,228,1)",
                                boxShadow: "0px 4px 12px rgba(102,82,228,0.4)",
                                transition: "all 0.3s linear",
                              },
                            }}
                          >
                            View
                          </Button>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Career Path Skeleton */}
                    <Grid item xs={12} sx={{ mt: { xs: 4, sm: 5, md: 6 } }}>
                      <Box
                        sx={{
                          p: { xs: "20px", sm: "25px", md: "30px" },
                          borderRadius: { xs: "24px", sm: "32px", md: "39px" },
                          background: "rgba(255, 255, 255, 0.05)",
                          border: "1px solid rgba(102,82,228,0.3)",
                          backdropFilter: "blur(10px)",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          minHeight: 400,
                          position: "relative",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily:
                              "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                            fontSize: { xs: "18px", sm: "20px", md: "22px" },
                            fontWeight: 600,
                            color: "#fff",
                            mb: 4,
                            textAlign: "center",
                          }}
                        >
                          Career Path
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 3,
                            width: "100%",
                            maxWidth: 400,
                          }}
                        >
                          {[...Array(4)].map((_, idx) => (
                            <React.Fragment key={idx}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  alignItems: "center",
                                  gap: 2,
                                  p: 2,
                                  width: "100%",
                                  borderRadius: 2,
                                  backgroundColor: "rgba(255, 255, 255, 0.03)",
                                  border: "1px solid rgba(102,82,228,0.2)",
                                }}
                              >
                                <Skeleton
                                  variant="circular"
                                  width={40}
                                  height={40}
                                  sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                                />
                                <Box sx={{ flex: 1 }}>
                                  <Skeleton
                                    height={24}
                                    width="80%"
                                    sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                                  />
                                  <Skeleton
                                    height={18}
                                    width="60%"
                                    sx={{
                                      mt: 0.5,
                                      bgcolor: "rgba(255, 255, 255, 0.1)",
                                    }}
                                  />
                                </Box>
                              </Box>
                              {idx < 3 && (
                                <Box
                                  sx={{
                                    width: 2,
                                    height: 20,
                                    backgroundColor: "rgba(102,82,228,0.3)",
                                  }}
                                />
                              )}
                            </React.Fragment>
                          ))}
                        </Box>
                        <Box
                          sx={{
                            mt: 4,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            variant="contained"
                            onClick={() => {
                              ReactGA4.event("view_insights_click", {
                                section: "career_path",
                              });
                              if (isLoggedIn) {
                                if (onViewFullInsights) {
                                  onViewFullInsights();
                                }
                              } else {
                                onLoginClick();
                              }
                            }}
                            sx={{
                              backgroundColor: "transparent !important",
                              backgroundImage:
                                "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1) )",
                              color: "#fff",
                              fontFamily:
                                "'Montserrat', 'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                              fontSize: { xs: "12px", sm: "13px", md: "14px" },
                              fontWeight: 600,
                              textTransform: "none",
                              borderRadius: "20px",
                              px: { xs: 2, sm: 2.5, md: 3 },
                              py: { xs: "6px", sm: "8px", md: "10px" },
                              boxShadow: "0px 2px 8px rgba(102,82,228,0.3)",
                              transition: "all 0.3s linear",
                              "&:hover": {
                                border: "1px solid rgba(74,61,158,1)",
                                background: "#FFF !important",
                                color: "rgba(108,82,228,1)",
                                boxShadow: "0px 4px 12px rgba(102,82,228,0.4)",
                                transition: "all 0.3s linear",
                              },
                            }}
                          >
                            View
                          </Button>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Top Interview Questions Skeleton */}
                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{ mt: { xs: 4, sm: 5, md: 6 } }}
                    >
                      <Box
                        sx={{
                          p: { xs: "20px", sm: "25px", md: "30px" },
                          borderRadius: { xs: "24px", sm: "32px", md: "39px" },
                          background: "rgba(255, 255, 255, 0.05)",
                          border: "1px solid rgba(102,82,228,0.3)",
                          backdropFilter: "blur(10px)",
                          minHeight: 300,
                          position: "relative",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily:
                              "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                            fontSize: { xs: "18px", sm: "20px", md: "22px" },
                            fontWeight: 600,
                            color: "#fff",
                            mb: 2,
                            textAlign: "center",
                          }}
                        >
                          Top Interview Questions
                        </Typography>
                        {[...Array(6)].map((_, index) => (
                          <Box key={index} sx={{ mt: 2 }}>
                            <Skeleton
                              width="100%"
                              height={20}
                              animation="wave"
                              sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                            />
                            <Skeleton
                              width="95%"
                              height={18}
                              animation="wave"
                              sx={{
                                mt: 0.5,
                                bgcolor: "rgba(255, 255, 255, 0.1)",
                              }}
                            />
                          </Box>
                        ))}
                        <Box
                          sx={{
                            mt: 3,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            variant="contained"
                            onClick={() => {
                              ReactGA4.event("view_insights_click", {
                                section: "salary",
                              });
                              if (isLoggedIn) {
                                if (onViewFullInsights) {
                                  onViewFullInsights();
                                }
                              } else {
                                onLoginClick();
                              }
                            }}
                            sx={{
                              backgroundColor: "transparent !important",
                              backgroundImage:
                                "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1) )",
                              color: "#fff",
                              fontFamily:
                                "'Montserrat', 'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                              fontSize: { xs: "12px", sm: "13px", md: "14px" },
                              fontWeight: 600,
                              textTransform: "none",
                              borderRadius: "20px",
                              px: { xs: 2, sm: 2.5, md: 3 },
                              py: { xs: "6px", sm: "8px", md: "10px" },
                              boxShadow: "0px 2px 8px rgba(102,82,228,0.3)",
                              transition: "all 0.3s linear",
                              "&:hover": {
                                border: "1px solid rgba(74,61,158,1)",
                                background: "#FFF !important",
                                color: "rgba(108,82,228,1)",
                                boxShadow: "0px 4px 12px rgba(102,82,228,0.4)",
                                transition: "all 0.3s linear",
                              },
                            }}
                          >
                            View
                          </Button>
                        </Box>
                      </Box>
                    </Grid>

                    {/* Game-Changers Skeleton */}
                    <Grid
                      item
                      xs={12}
                      md={6}
                      sx={{ mt: { xs: 4, sm: 5, md: 6 } }}
                    >
                      <Box
                        sx={{
                          p: { xs: "20px", sm: "25px", md: "30px" },
                          borderRadius: { xs: "24px", sm: "32px", md: "39px" },
                          background: "rgba(255, 255, 255, 0.05)",
                          border: "1px solid rgba(102,82,228,0.3)",
                          backdropFilter: "blur(10px)",
                          minHeight: 300,
                          position: "relative",
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily:
                              "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                            fontSize: { xs: "18px", sm: "20px", md: "22px" },
                            fontWeight: 600,
                            color: "#fff",
                            mb: 2,
                            textAlign: "center",
                          }}
                        >
                          Game-Changers That Could Redefine Your Career
                        </Typography>
                        {[...Array(6)].map((_, index) => (
                          <Box key={index} sx={{ mt: 2 }}>
                            <Skeleton
                              width="100%"
                              height={20}
                              animation="wave"
                              sx={{ bgcolor: "rgba(255, 255, 255, 0.1)" }}
                            />
                            <Skeleton
                              width="90%"
                              height={18}
                              animation="wave"
                              sx={{
                                mt: 0.5,
                                bgcolor: "rgba(255, 255, 255, 0.1)",
                              }}
                            />
                          </Box>
                        ))}
                        <Box
                          sx={{
                            mt: 3,
                            display: "flex",
                            justifyContent: "center",
                          }}
                        >
                          <Button
                            variant="contained"
                            onClick={() => {
                              ReactGA4.event("view_insights_click", {
                                section: "interview_questions",
                              });
                              if (isLoggedIn) {
                                if (onViewFullInsights) {
                                  onViewFullInsights();
                                }
                              } else {
                                onLoginClick();
                              }
                            }}
                            sx={{
                              backgroundColor: "transparent !important",
                              backgroundImage:
                                "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1) )",
                              color: "#fff",
                              fontFamily:
                                "'Montserrat', 'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                              fontSize: { xs: "12px", sm: "13px", md: "14px" },
                              fontWeight: 600,
                              textTransform: "none",
                              borderRadius: "20px",
                              px: { xs: 2, sm: 2.5, md: 3 },
                              py: { xs: "6px", sm: "8px", md: "10px" },
                              boxShadow: "0px 2px 8px rgba(102,82,228,0.3)",
                              transition: "all 0.3s linear",
                              "&:hover": {
                                border: "1px solid rgba(74,61,158,1)",
                                background: "#FFF !important",
                                color: "rgba(108,82,228,1)",
                                boxShadow: "0px 4px 12px rgba(102,82,228,0.4)",
                                transition: "all 0.3s linear",
                              },
                            }}
                          >
                            View
                          </Button>
                        </Box>
                      </Box>
                    </Grid>
                  </>
                )}

                {/* CTA Section */}
                <Grid item xs={12} md={12}>
                  <Box
                    sx={{
                      border: "1px solid rgba(102,82,228,0.3)",
                      borderRadius: { xs: "24px", sm: "32px", md: "39px" },
                      background: "rgba(102,82,228,0.1)",
                      backdropFilter: "blur(10px)",
                      p: { xs: 3, sm: 4, md: 5 },
                      textAlign: "center",
                      mt: { xs: 2, sm: 3 },
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily:
                          "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        fontSize: {
                          xs: "20px",
                          sm: "24px",
                          md: "28px",
                          lg: "32px",
                        },
                        fontWeight: 600,
                        color: "#fff",
                        mb: { xs: 1.5, sm: 2 },
                      }}
                    >
                      {isLoggedIn
                        ? "View Full Insights"
                        : "Unlock Full Insights"}
                    </Typography>
                    <Typography
                      sx={{
                        fontFamily:
                          "'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        fontSize: { xs: "13px", sm: "14px", md: "16px" },
                        color: "rgba(255, 255, 255, 0.8)",
                        mb: { xs: 3, sm: 4 },
                        px: { xs: 1, sm: 0 },
                      }}
                    >
                      {isLoggedIn
                        ? "Get detailed insights including interview questions, game-changer insights, salary data, and more!"
                        : "Login to access detailed insights including interview questions, game-changer insights, salary data, and more!"}
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => {
                        ReactGA4.event("view_full_insights_click");
                        if (isLoggedIn) {
                          if (onViewFullInsights) {
                            onViewFullInsights();
                          }
                        } else {
                          onLoginClick();
                        }
                      }}
                      sx={{
                        backgroundColor: "transparent !important",
                        backgroundImage:
                          "linear-gradient(to right, rgba(74,61,158,1), rgba(108,82,229,1) )",
                        color: "#fff",
                        fontFamily:
                          "'Montserrat', 'SF Pro Display', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                        fontSize: { xs: "14px", sm: "16px", md: "18px" },
                        fontWeight: 700,
                        textTransform: "none",
                        borderRadius: "30px",
                        px: { xs: 3, sm: 4, md: 5 },
                        py: { xs: "10px", sm: "12px", md: "14px" },
                        boxShadow: "0px 4px 12px rgba(102,82,228,0.3)",
                        transition: "all 0.3s linear",
                        "&:hover": {
                          border: "1px solid rgba(74,61,158,1)",
                          background: "#FFF !important",
                          color: "rgba(108,82,228,1)",
                          boxShadow: "0px 6px 16px rgba(102,82,228,0.4)",
                          transition: "all 0.3s linear",
                        },
                      }}
                    >
                      {isLoggedIn
                        ? "View Full Insights"
                        : "Login to View Full Insights"}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          ) : null}
        </Grid>
      </Grid>
    </Box>
  );
}

export default BasicRelevanceCheckDark;
